import type { ParsedRole } from '@rhia/protocol';
import type { ReactNode } from 'react';
import { Navigate, useLocation, useParams } from 'react-router';
import { useAuth } from '@/features/auth/AuthProvider';
import { useWorkspaceBySlug } from '@/features/workspaces';
import { PendingApproval } from './PendingApproval';

export function RequireAuth({ children }: { children: ReactNode }) {
  const { state } = useAuth();
  const location = useLocation();
  if (state.status === 'loading') return <p className="p-6 text-sm">checking session…</p>;
  if (state.status === 'unauthenticated') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  // Self-serve signups await admin approval before they can reach any
  // protected route (the server enforces this too — see requireApproved).
  if (state.current.user.approval_status === 'pending') {
    return <PendingApproval />;
  }
  return <>{children}</>;
}

/**
 * Gates a route on the user being SuperAdmin. Use for /admin/* routes.
 */
export function RequireSuperAdmin({ children }: { children: ReactNode }) {
  const { state } = useAuth();
  if (state.status === 'loading') return <p className="p-6 text-sm">checking session…</p>;
  if (state.status === 'unauthenticated') return <Navigate to="/login" replace />;
  if (!state.current.user.is_super_admin) {
    return (
      <div className="mx-auto mt-12 max-w-md rounded border border-[color:var(--color-warning)] p-6 text-center">
        <h2 className="mb-2 text-lg font-bold">Forbidden</h2>
        <p className="text-sm text-[color:var(--color-fg)]/70">
          This page requires the SuperAdmin role.
        </p>
      </div>
    );
  }
  return <>{children}</>;
}

/**
 * Gates a route on the user having at least the given role in the
 * current workspace (resolved via :slug param).
 */
export function RequireWorkspaceRole({ min, children }: { min: ParsedRole; children: ReactNode }) {
  const { state, hasRole } = useAuth();
  const { slug } = useParams<{ slug: string }>();
  const { data: ws } = useWorkspaceBySlug(slug ?? '');
  const wsId = ws?.id ?? null;
  if (state.status === 'loading') return <p className="p-6 text-sm">checking session…</p>;
  if (state.status === 'unauthenticated') return <Navigate to="/login" replace />;
  if (!wsId) {
    return <p className="p-6 text-sm">workspace not found: {slug}</p>;
  }
  if (!hasRole(wsId, min)) {
    return (
      <div className="mx-auto mt-12 max-w-md rounded border border-[color:var(--color-warning)] p-6 text-center">
        <h2 className="mb-2 text-lg font-bold">Forbidden</h2>
        <p className="text-sm text-[color:var(--color-fg)]/70">
          This page requires <code>{min}</code> or higher in this workspace.
        </p>
      </div>
    );
  }
  return <>{children}</>;
}
